import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listServices,
  getService,
  createService,
  updateService,
  deleteService,
  updateRouting,
} from '@/lib/api/services';
import type {
  Service,
  ServiceListResponse,
  CreateServiceRequest,
  UpdateServiceRequest,
  ListServicesParams,
  FallbackChainEntry,
} from '@/types/service';

/** Query key factory for services */
export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (params?: ListServicesParams) => [...serviceKeys.lists(), params] as const,
  details: () => [...serviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceKeys.details(), id] as const,
};

/**
 * Hook to fetch a paginated list of services.
 *
 * @param params Optional query parameters for filtering/pagination
 */
export function useServices(params?: ListServicesParams) {
  return useQuery<ServiceListResponse, Error>({
    queryKey: serviceKeys.list(params),
    queryFn: () => listServices(params),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to fetch a single service by ID.
 * Spec 0027: Optional merchantId hint for platform_admin Facade routing.
 * Query-Key stays without merchantId — same data regardless of routing path.
 *
 * @param serviceId The service ID to fetch
 * @param merchantId Optional merchantId hint (from route-state)
 */
export function useService(serviceId: string, merchantId?: string) {
  return useQuery<Service, Error>({
    queryKey: serviceKeys.detail(serviceId),
    queryFn: () => getService(serviceId, merchantId),
    enabled: !!serviceId,
  });
}

/**
 * Hook to fetch only active services for use in dropdowns.
 *
 * @example
 * const { data, isLoading, error } = useActiveServices();
 */
export function useActiveServices() {
  return useServices({ status: 'active' });
}

/**
 * Hook to create a new service.
 * Invalidates service list cache on success.
 */
export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation<Service, Error, CreateServiceRequest>({
    mutationFn: createService,
    onSuccess: () => {
      // Invalidate all service list queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing service.
 * Invalidates both the specific service and list cache on success.
 */
export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation<Service, Error, { serviceId: string; data: UpdateServiceRequest }>({
    mutationFn: ({ serviceId, data }) => updateService(serviceId, data),
    onSuccess: (updatedService) => {
      // Update the specific service in cache
      queryClient.setQueryData(serviceKeys.detail(updatedService.serviceId), updatedService);
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

/**
 * Spec 0216: Mutation-Hook fuer das Speichern eines Routing-Updates.
 *
 * Sendet `fallbackChain` + `msisdnCollectionUrl` als Partial-Update und
 * invalidiert die Service-Detail- und Listen-Caches bei Erfolg.
 */
export function useUpdateRouting(serviceId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    Service,
    Error,
    { fallbackChain: FallbackChainEntry[]; msisdnCollectionUrl: string | null }
  >({
    mutationFn: (vars) =>
      updateRouting(serviceId, vars.fallbackChain, vars.msisdnCollectionUrl),
    onSuccess: (updatedService) => {
      queryClient.setQueryData(serviceKeys.detail(updatedService.serviceId), updatedService);
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

/**
 * Hook to delete (deactivate) a service.
 * Invalidates both the specific service and list cache on success.
 */
export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation<Service, Error, string>({
    mutationFn: deleteService,
    onSuccess: (deactivatedService) => {
      // Update the specific service in cache with deactivated status
      queryClient.setQueryData(
        serviceKeys.detail(deactivatedService.serviceId),
        deactivatedService
      );
      // Invalidate list queries to refetch
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}
